import { useState, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";

export default function useProductos(
  initial = [],
  initialDescuento = 0,
  catalogo = [],
  initialDescMode = "%",
  ivaPct = 16
) {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;

  const [datos, setDatos] = useState(() =>
    initial.length > 0
      ? initial.map((p) => ({ id: uuidv4(), ...p }))
      : [
          {
            id: uuidv4(),
            nombre: "",
            descripcion: "",
            numeroDeParteProveedor: "",
            cantidad: 1,
            tarifa: 0,
            iva: 0.16,
            descuento: 0,
            tipoDescuento: "%",
            importe: 0,
          },
        ]
  );

  const [descuento, setDescuento] = useState(initialDescuento);
  const [descMode, setDescMode] = useState(initialDescMode);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [activeRow, setActiveRow] = useState(null);

  const añadirFila = useCallback((e) => {
    e?.preventDefault();
    setDatos((prev) => [
      ...prev,
      {
        id: uuidv4(),
        nombre: "",
        descripcion: "",
        numeroDeParteProveedor: "",
        cantidad: 1,
        tarifa: 0,
        iva: 0.16,
        descuento: 0,
        tipoDescuento: "%",
        importe: 0,
      },
    ]);
  }, []);

  const eliminar = useCallback((id) => {
    setDatos((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const actualizarCantidad = useCallback((id, cantidad) => {
    setDatos((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, cantidad, importe: p.tarifa * cantidad } : p
      )
    );
  }, []);

  const moveProducto = useCallback((fromIndex, toIndex) => {
    setDatos((prev) => {
      const updated = [...prev];

      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= updated.length ||
        toIndex >= updated.length
      ) {
        return prev;
      }

      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const calcularSubtotal = useCallback(() => {
    return datos.reduce((sum, p) => {
      const tarifa = Number(p.tarifa) || 0;
      const cantidad = Number(p.cantidad) || 0;
      const base = tarifa * cantidad;

      const d = Number(p.descuento) || 0;
      const tipo = p.tipoDescuento || "%";

      let descLinea =
        tipo === "%"
          ? base * (d / 100)
          : d;

      if (descLinea > base) descLinea = base;

      const subtotalConDesc = base - descLinea;

      return sum + subtotalConDesc;
    }, 0);
  }, [datos]);

  const obtenerIVA = useCallback(() => {
    const pct = Number(ivaPct);
    const tasa = Number.isFinite(pct) ? pct / 100 : 0.16;
    return tasa;
  }, [ivaPct]);

  const subtotalConDesc = useMemo(() => {
    const sub = calcularSubtotal();
    if (descMode === "$") {
      return Math.max(sub - descuento, 0);
    } else {
      return Math.max(sub - (sub * descuento) / 100, 0);
    }
  }, [calcularSubtotal, descuento, descMode]);

  const subtotalBase = useMemo(() => {
    return datos.reduce((sum, p) => {
      const tarifa = Number(p.tarifa) || 0;
      const cantidad = Number(p.cantidad) || 0;
      return sum + tarifa * cantidad;
    }, 0);
  }, [datos]);

  const totalDescuentoGlobal = useMemo(() => {
    const diff = subtotalBase - subtotalConDesc;
    return diff > 0 ? diff : 0;
  }, [subtotalBase, subtotalConDesc]);

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const iva = useMemo(
    () => round2(subtotalConDesc * obtenerIVA()),
    [subtotalConDesc, obtenerIVA]
  );

  const total = useMemo(
    () => round2(subtotalConDesc + iva),
    [subtotalConDesc, iva]
  );

  const manejarCambioDescuento = useCallback(
    (e) => {
      let v = e.target.value;
      if (/^\d*\.?\d*$/.test(v)) {
        let n = parseFloat(v);
        if (isNaN(n)) n = 0;
        if (descMode === "%" && n > 100) n = 100;
        setDescuento(n);
      }
    },
    [descMode]
  );

  const toggleDescMode = useCallback(() => {
    const sub = calcularSubtotal();

    if (sub <= 0) {
      setDescMode((m) => (m === "%" ? "$" : "%"));
      return;
    }

    setDescMode((modoActual) => {
      if (modoActual === "%") {
        const pct = Number(descuento) || 0;
        const monto = (sub * pct) / 100;
        setDescuento(parseFloat(monto.toFixed(2)));
        return "$";
      } else {
        const monto = Number(descuento) || 0;
        const pct = sub > 0 ? (monto / sub) * 100 : 0;
        setDescuento(parseFloat(pct.toFixed(2)));
        return "%";
      }
    });
  }, [calcularSubtotal, descuento]);

  const agregarBulk = useCallback((articulos) => {
    console.log("Agregando artículos a granel:", articulos);
  }, []);

  const handleInputChange = useCallback(
    (e, productoId) => {
      const name = e?.target?.name || "";
      const rawVal = e?.target?.value ?? "";
      const val = typeof rawVal === "string" ? rawVal : String(rawVal);

      // Helpers
      const toNumber = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      // ✅ 1) Campos que NO son "nombre": solo actualiza ese campo y NO hagas autocomplete
      const nonSearchFields = new Set([
        "sku",
        "descripcion",
        "tarifa",
        "descuento",
        "tipoDescuento",
        "numeroDeParteProveedor",
        "unidad",
        "iva",
      ]);

      if (name && nonSearchFields.has(name)) {
        setDatos((prev) =>
          prev.map((i) => {
            if (i.id !== productoId) return i;

            // Normaliza numéricos
            if (name === "tarifa" || name === "descuento" || name === "iva") {
              return { ...i, [name]: toNumber(val) };
            }

            return { ...i, [name]: val };
          })
        );

        // No tocar autocomplete aquí
        return;
      }

      // ✅ 2) Aquí solo entra cuando estás escribiendo en "nombre" (Producto)
      const q = val.trim().toLowerCase();
      const base = Array.isArray(catalogo) ? catalogo : [];

      const filtered = q
        ? base.filter((p) => {
            const nombre = (p.nombre || "").toLowerCase();
            const desc = (p.descripcion || p["Item Desc"] || "").toLowerCase();
            const parte = (p.numeroDeParteProveedor || "").toLowerCase();
            return nombre.includes(q) || desc.includes(q) || parte.includes(q);
          })
        : base;

      setFilteredProductos(filtered);
      setActiveRow(productoId);

      const found = base.find((p) => (p.nombre || "").toLowerCase() === q);

      setDatos((prev) => {
        if (found) {
          const existing = prev.find(
            (i) => i.nombre === found.nombre && i.id !== productoId
          );

          if (existing) {
            const curr = prev.find((i) => i.id === productoId);
            const sum = (existing.cantidad || 0) + (curr?.cantidad || 1);

            return prev
              .map((i) =>
                i.id === existing.id
                  ? {
                      ...i,
                      cantidad: sum,
                      tarifa: found.costo ?? i.tarifa ?? 0,
                      importe: (found.costo ?? i.tarifa ?? 0) * sum,
                      numeroDeParteProveedor:
                        found.numeroDeParteProveedor ??
                        i.numeroDeParteProveedor ??
                        "",
                    }
                  : i
              )
              .filter((i) => i.id !== productoId);
          }

          return prev.map((i) =>
            i.id === productoId
              ? {
                  ...i,
                  nombre: found.nombre || "",
                  descripcion: found["Item Desc"] || found.descripcion || "",
                  numeroDeParteProveedor: found.numeroDeParteProveedor || "",
                  tarifa: found.costo ?? 0,
                  iva: i.iva ?? 0.16,
                  importe: (found.costo ?? 0) * (i.cantidad || 0),
                }
              : i
          );
        }

        // ✅ Si NO encontró producto, solo actualiza NOMBRE (no descripción)
        return prev.map((i) =>
          i.id === productoId ? { ...i, nombre: val } : i
        );
      });
    },
    [catalogo]
  );

  const handleFileChange = useCallback(
    async (e, productoId) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const storage = getStorage();
        const fileName = `${Date.now()}_${file.name}`;
        const archivoRef = ref(
          storage,
          `productosClientes/${ownerUid}/${fileName}`
        );

        await uploadBytes(archivoRef, file);
        const url = await getDownloadURL(archivoRef);

        setDatos((prev) =>
          prev.map((item) =>
            item.id === productoId ? { ...item, imagenUrl: url } : item
          )
        );
      } catch (err) {
        console.error("Error subiendo la imagen", err);
        Swal.fire({
          icon: "error",
          title: "Error al subir la imagen",
          text: err.message,
        });
      }
    },
    [ownerUid, setDatos]
  );

  return {
    datos,
    descuento,
    descMode,
    añadirFila,
    eliminar,
    actualizarCantidad,
    calcularSubtotal,
    subtotalConDesc,
    iva,
    total,
    manejarCambioDescuento,
    toggleDescMode,
    obtenerIVA,
    agregarBulk,
    handleInputChange,
    filteredProductos,
    activeRow,
    setDatos,
    setDescuento,
    setFilteredProductos,
    handleFileChange,
    totalDescuentoGlobal,
    moveProducto,
    subtotalBase,
  };
}